import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Campaigns from "@/pages/Campaigns";
import CampaignNew from "@/pages/CampaignNew";
import CampaignDetail from "@/pages/CampaignDetail";
import Calls from "@/pages/Calls";
import FollowUps from "@/pages/FollowUps";
import Performance from "@/pages/Performance";
import Consent from "@/pages/Consent";
import Feedback from "@/pages/Feedback";
import Blocked from "@/pages/Blocked";
import DNC from "@/pages/DNC";
import Settings from "@/pages/Settings";
import Appointments from "@/pages/Appointments";
import Clients from "@/pages/Clients";
import ClientNew from "@/pages/ClientNew";
import ClientEdit from "@/pages/ClientEdit";
import AdminReferrals from "@/pages/AdminReferrals";
import AdminPersonas from "@/pages/AdminPersonas";
import Preview from "@/pages/Preview";
import Layout from "@/components/Layout";
import ProtectedRoute from "@/components/ProtectedRoute";

// Portal (white-label client) shell
import PortalLogin from "@/pages/portal/PortalLogin";
import PortalChangePassword from "@/pages/portal/PortalChangePassword";
import PortalDashboard from "@/pages/portal/PortalDashboard";
import PortalCalls from "@/pages/portal/PortalCalls";
import PortalAppointments from "@/pages/portal/PortalAppointments";
import PortalPerformance from "@/pages/portal/PortalPerformance";
import PortalAgent from "@/pages/portal/PortalAgent";
import PortalTickets from "@/pages/portal/PortalTickets";
import AdminTickets from "@/pages/AdminTickets";
import PortalLayout from "@/components/PortalLayout";
import PortalProtectedRoute from "@/components/PortalProtectedRoute";

const Protected = ({ children }) => (
  <ProtectedRoute>
    <Layout>{children}</Layout>
  </ProtectedRoute>
);

// Portal route wrapper — slug comes from URL via useParams() inside the components
const PortalRoute = ({ slug, children }) => (
  <PortalProtectedRoute slug={slug}>
    <PortalLayout>{children}</PortalLayout>
  </PortalProtectedRoute>
);

// Bridge that pulls slug from URL params before rendering portal routes
function PortalShell({ children }) {
  const { slug } = useParams();
  return <PortalRoute slug={slug}>{children}</PortalRoute>;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          {/* Admin */}
          <Route path="/login" element={<Login />} />
          <Route path="/" element={<Protected><Dashboard /></Protected>} />
          <Route path="/campaigns" element={<Protected><Campaigns /></Protected>} />
          <Route path="/campaigns/new" element={<Protected><CampaignNew /></Protected>} />
          <Route path="/campaigns/:id" element={<Protected><CampaignDetail /></Protected>} />
          <Route path="/calls" element={<Protected><Calls /></Protected>} />
          <Route path="/follow-ups" element={<Protected><FollowUps /></Protected>} />
          <Route path="/performance" element={<Protected><Performance /></Protected>} />
          <Route path="/consent" element={<Protected><Consent /></Protected>} />
          <Route path="/feedback" element={<Protected><Feedback /></Protected>} />
          <Route path="/blocked" element={<Protected><Blocked /></Protected>} />
          <Route path="/dnc" element={<Protected><DNC /></Protected>} />
          <Route path="/appointments" element={<Protected><Appointments /></Protected>} />
          <Route path="/settings" element={<Protected><Settings /></Protected>} />
          <Route path="/clients" element={<Protected><Clients /></Protected>} />
          <Route path="/clients/new" element={<Protected><ClientNew /></Protected>} />
          <Route path="/clients/:id" element={<Protected><ClientEdit /></Protected>} />
          <Route path="/referrals" element={<Protected><AdminReferrals /></Protected>} />
          <Route path="/agent-personas" element={<Protected><AdminPersonas /></Protected>} />
          <Route path="/tickets" element={<Protected><AdminTickets /></Protected>} />

          {/* Portal — public branded login + protected scoped pages */}
          <Route path="/portal/:slug/login" element={<PortalLogin />} />
          <Route path="/portal/:slug/change-password" element={<PortalShell><PortalChangePassword /></PortalShell>} />
          <Route path="/portal/:slug" element={<PortalShell><PortalDashboard /></PortalShell>} />
          <Route path="/portal/:slug/calls" element={<PortalShell><PortalCalls /></PortalShell>} />
          <Route path="/portal/:slug/appointments" element={<PortalShell><PortalAppointments /></PortalShell>} />
          <Route path="/portal/:slug/performance" element={<PortalShell><PortalPerformance /></PortalShell>} />
          <Route path="/portal/:slug/agent" element={<PortalShell><PortalAgent /></PortalShell>} />
          <Route path="/portal/:slug/tickets" element={<PortalShell><PortalTickets /></PortalShell>} />

          {/* Public preview link from welcome email — no login needed */}
          <Route path="/preview/:token" element={<Preview />} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
